import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, Globe } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Button } from '../ui/Button';

const Header = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDivisionsOpen, setIsDivisionsOpen] = useState(false);
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(newLang);
  };

  const navItems = [
    { key: 'home', path: '/' },
    { key: 'about', path: '/about' },
    {
      key: 'divisions',
      path: '#',
      hasDropdown: true,
      dropdownItems: [
        { key: 'telecommunication', path: '/divisions/telecommunication' },
        { key: 'power', path: '/divisions/power' },
        { key: 'hazardous', path: '/divisions/hazardous' },
        { key: 'commercial', path: '/divisions/commercial' },
      ]
    },
    { key: 'projects', path: '/projects' },
    { key: 'clients', path: '/clients-industries' },
    { key: 'contact', path: '/contact' },
  ];

  const isActivePath = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <motion.header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white shadow-lg'
      )}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="container-max">
        <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img 
              src="/logo.svg" 
              alt="Global Technology" 
              className="h-14 w-50"
            />
            <span className="text-global-green font-bold">Global Technology</span>
            
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <div key={item.key} className="relative">
                {item.hasDropdown ? (
                  <div
                    className="relative"
                    onMouseEnter={() => setIsDivisionsOpen(true)}
                    onMouseLeave={() => setIsDivisionsOpen(false)}
                  >
                    <button
                      className={cn(
                        'flex items-center space-x-1 px-3 py-2 rounded-lg transition-colors duration-200',
                        location.pathname.includes('/divisions')
                          ? 'text-white bg-gradient-to-r from-global-green to-global-light-blue'
                          : 'text-charcoal hover:text-white hover:bg-gradient-to-r hover:from-global-green hover:to-global-light-blue'
                      )}
                    >
                      <span>{t(`nav.${item.key}`)}</span>
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    
                    <AnimatePresence>
                      {isDivisionsOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-slate py-2"
                        >
                          {item.dropdownItems.map((dropdownItem) => (
                            <Link
                              key={dropdownItem.key}
                              to={dropdownItem.path}
                              className={cn(
                        'block px-4 py-2 text-sm transition-colors duration-200',
                        isActivePath(dropdownItem.path)
                          ? 'text-white bg-gradient-to-r from-global-green to-global-light-blue'
                          : 'text-charcoal hover:text-white hover:bg-gradient-to-r hover:from-global-green hover:to-global-light-blue'
                      )}
                            >
                              {t(`nav.${dropdownItem.key}`)}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    className={cn(
                      'px-3 py-2 rounded-lg transition-colors duration-200',
                      isActivePath(item.path)
                        ? 'text-global-green bg-gradient-to-r from-global-green to-global-light-blue text-white'
                        : 'text-charcoal hover:text-white hover:bg-gradient-to-r hover:from-global-green hover:to-global-light-blue'
                    )}
                  >
                    {t(`nav.${item.key}`)}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          <div className="flex items-center space-x-4 ">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="flex items-center space-x-2"
            >
              <Globe className="w-4 h-4 bg" />
              <span>{t('nav.language')}</span>
            </Button>

            <button
              className="lg:hidden p-2 rounded-lg hover:bg-gradient-to-r from-global-green to-global-light-blue text-charcoal"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white border-t border-slate"
            >
              <nav className="px-4 py-4 space-y-2">
                {navItems.map((item) => (
                  <div key={item.key}>
                    {item.hasDropdown ? (
                      <div>
                        <div className="px-3 py-2 text-charcoal font-medium">
                          {t(`nav.${item.key}`)}
                        </div>
                        <div className={cn('space-y-1', isRTL ? 'mr-4' : 'ml-4')}>
                          {item.dropdownItems.map((dropdownItem) => (
                            <Link
                              key={dropdownItem.key}
                              to={dropdownItem.path}
                              className={cn(
                                'block px-3 py-2 rounded-lg transition-colors duration-200',
                                isActivePath(dropdownItem.path)
                                  ? 'text-white bg-gradient-to-r from-global-green to-global-light-blue'
                                  : 'text-charcoal hover:text-white hover:bg-gradient-to-r from-global-green to-global-light-blue'
                              )}
                              onClick={() => setIsMenuOpen(false)}
                            >
                              {t(`nav.${dropdownItem.key}`)}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Link
                        to={item.path}
                        className={cn(
                          'block px-3 py-2 rounded-lg transition-colors duration-200',
                          isActivePath(item.path)
                            ? 'text-white bg-gradient-to-r from-global-green to-global-light-blue'
                            : 'text-charcoal hover:text-white hover:bg-gradient-to-r from-global-green to-global-light-blue'
                        )}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {t(`nav.${item.key}`)}
                      </Link>
                    )}
                  </div>
                ))}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Header;